import { useCallback } from 'react';
import { useGetOrganizationSettingsQuery } from '../services';
import { LD_FeatureFlags, Permissions, ProspectCategory } from '../types';
import useAppSelector from './useAppSelector';
import useFeatureFlag from './useFeatureFlag';

const useGetUserPermissions = () => {
  const { data: orgConfigs, isLoading } = useGetOrganizationSettingsQuery({});
  const { repCanEditLibrary = false, repCanViewLibrary = false, repCanCreateSims = false } = orgConfigs || {};

  const salesloftFF = useFeatureFlag(LD_FeatureFlags.RELEASE_SALESLOFT_INTEGRATION);

  const { organization, user } = useAppSelector((state) => state.auth);
  const { id: userId, permissions: userPermissions = [] } = user || {};

  const isSelfServiceEnabled = !!organization?.isSelfServiceEnabled;

  const canManageSims = userPermissions.includes(Permissions.MANAGE_PROSPECT);
  const canViewExternalIntegrations = userPermissions.includes(Permissions.VIEW_EXTERNAL_INTEGRATION);

  // Creating a sim includes:
  // 1. Creating a new sim
  // 2. Editing their own draft simulations
  const canUserCreateSims = isSelfServiceEnabled && (canManageSims || repCanCreateSims);

  // Editing library content includes:
  // 1. Creating/editing/deleting folders in the library
  // 2. Publishing their own draft simulations to the library
  const canUserEditLibrary = canManageSims || repCanEditLibrary;

  // Viewing library content includes:
  // 1. Viewing the library page
  const canUserViewLibrary = canManageSims || repCanViewLibrary;

  // A user can view an external integration if they have either of the following:
  // 1. View external integrations permission
  // 2. Create sims permission and the external integration is enabled for the organization
  const canUserViewGong = canViewExternalIntegrations || (!!organization?.gongAuth && repCanCreateSims);

  const canUserViewSalesloft =
    salesloftFF && (canViewExternalIntegrations || (!!organization?.salesloftAuth && repCanCreateSims));

  // A user can edit a prospect if they have either of the following:
  // 1. Manage prospect permission
  // 2. Create sims permission and they're editing their own draft sim
  // 3. Edit library permission and the sim is a practice sim
  // Editing a sim includes:
  // 1. Modifying the sim outside of self-service
  const getCanUserEditSim = useCallback(
    (simCategory: ProspectCategory, simIsDraft: boolean, simUserId: string) => {
      const isUserDraftSim = simUserId === userId && repCanCreateSims && simIsDraft;
      const canEditPracticeSim = repCanEditLibrary && simCategory === ProspectCategory.PRACTICE;
      return canManageSims || isUserDraftSim || canEditPracticeSim;
    },
    [canManageSims, userId, repCanCreateSims, repCanEditLibrary]
  );

  // Editing a sim in self-service includes:
  // 1. Modifying the sim inside self-service
  // 2. Archiving/deleting sims
  const getCanUserEditSimInSelfService = useCallback(
    (simCategory: ProspectCategory, simIsDraft: boolean, simUserId: string) => {
      const canUserEditSim = getCanUserEditSim(simCategory, simIsDraft, simUserId);
      return isSelfServiceEnabled && canUserEditSim;
    },
    [isSelfServiceEnabled, getCanUserEditSim]
  );

  return {
    canUserCreateSims,
    canUserEditLibrary,
    canUserViewLibrary,
    canUserViewGong,
    canUserViewSalesloft,
    getCanUserEditSim,
    getCanUserEditSimInSelfService,
    isLoading,
  };
};

export default useGetUserPermissions;
